import type { MateriaalLine, StockMove, StockPicking } from '@/types/odoo';
import { productName, unmatchedMoves } from '@/utils/materiaal';
import { groupByCateg } from '@/utils/materiaalCateg';

interface WerfMateriaalTabProps {
  pickings: StockPicking[];
  lines: MateriaalLine[];
  moves: StockMove[];
  loading: boolean;
}

interface PickingWithLines {
  picking: StockPicking;
  lines: MateriaalLine[];
  /** Items added straight to the picking in Odoo after the oplijst upload —
   *  invisible in the authored lines, but they're on the actual delivery so the
   *  warehouse must pick them. Rendered in a "Toegevoegd in Odoo" block. */
  addedMoves: StockMove[];
}

function eenheidLabel(line: MateriaalLine): string {
  return line.x_eenheid && line.x_eenheid[1] ? line.x_eenheid[1] : '';
}

function moveEenheidLabel(move: StockMove): string {
  return move.product_uom && move.product_uom[1] ? move.product_uom[1] : '';
}

function groupLinesByPicking(
  pickings: StockPicking[],
  lines: MateriaalLine[],
  moves: StockMove[],
): PickingWithLines[] {
  return pickings
    .map((picking) => ({
      picking,
      lines: lines
        .filter((l) => l.x_levering && l.x_levering[0] === picking.id)
        .sort((a, b) => (a.x_studio_sequence ?? 0) - (b.x_studio_sequence ?? 0)),
      // Only OUT deliveries have authored lines to diff against. Retours (IN) are
      // moves-only by nature — every move would falsely read as "added in Odoo" —
      // so leave them empty, preserving the prior no-lines-→-dropped behavior.
      addedMoves: picking.picking_type_code === 'incoming' ? [] : unmatchedMoves(picking, lines, moves),
    }))
    // Keep a picking if it has authored lines OR items added directly in Odoo —
    // a backorder / all-added picking has no lines but must still be picked.
    .filter((g) => g.lines.length > 0 || g.addedMoves.length > 0);
}

function MateriaalLijstCard({ group, isRetour }: { group: PickingWithLines; isRetour: boolean }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-3.5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-sm font-bold text-td-navy min-w-0">
          <svg className="w-3.5 h-3.5 text-td-blue shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 12h6M9 16h6M9 8h6M5 4h14a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
          </svg>
          <span className="truncate">{group.picking.name}</span>
        </span>
        <div className="shrink-0 flex items-center gap-1.5">
          {group.picking.create_date && (
            <span className="text-xs text-td-muted">
              {new Date(group.picking.create_date).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
          {isRetour && (
            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800">
              Retour
            </span>
          )}
        </div>
      </div>
      <div>
        {groupByCateg(group.lines).map((cat) => (
          <div key={cat.categId ?? 'overige'}>
            <div className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gray-50/80 border-b border-gray-100">
              <span className={`w-2 h-2 rounded-full shrink-0 ${cat.color}`} aria-hidden="true" />
              <span className="flex-1 text-[11px] font-bold uppercase tracking-wide text-td-muted truncate">
                {cat.label}
              </span>
              <span className="shrink-0 text-[10px] font-semibold text-td-light">
                {cat.count} {cat.count === 1 ? 'artikel' : 'artikelen'}
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {cat.items.map((line) => (
                <div key={line.id} className="flex items-center gap-2 px-3.5 py-2">
                  <p className="flex-1 text-[13px] text-td-navy truncate">{productName(line)}</p>
                  <span className="shrink-0 text-[13px] font-bold text-td-navy">
                    {line.x_aantal}{' '}
                    {eenheidLabel(line) && (
                      <span className="text-[10px] font-semibold text-td-light">{eenheidLabel(line)}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Items added straight to the picking in Odoo after the oplijst upload —
            not in the authored lines, but on the delivery, so they must be picked. */}
        {group.addedMoves.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 px-3.5 py-1.5 bg-amber-50 border-b border-amber-100">
              <span className="w-2 h-2 rounded-full shrink-0 bg-amber-400" aria-hidden="true" />
              <span className="flex-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 truncate">
                Toegevoegd in Odoo
              </span>
              <span className="shrink-0 text-[10px] font-semibold text-amber-600">
                {group.addedMoves.length} {group.addedMoves.length === 1 ? 'artikel' : 'artikelen'}
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {group.addedMoves.map((move) => (
                <div key={move.id} className="flex items-center gap-2 px-3.5 py-2">
                  <p className="flex-1 text-[13px] text-td-navy truncate">
                    {move.product_id ? move.product_id[1] : (move.description_picking || 'Onbekend product')}
                  </p>
                  <span className="shrink-0 text-[13px] font-bold text-td-navy">
                    {Number.isInteger(move.product_uom_qty) ? move.product_uom_qty : move.product_uom_qty.toFixed(2)}{' '}
                    {moveEenheidLabel(move) && (
                      <span className="text-[10px] font-semibold text-td-light">{moveEenheidLabel(move)}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only materiaal tab: groups per materiaallijst (product + aantal + eenheid).
 * Retours (incoming pickings, werf → magazijn) are shown separately at the bottom,
 * each labeled "Retour".
 */
export function WerfMateriaalTab({ pickings, lines, moves, loading }: WerfMateriaalTabProps) {
  if (loading) {
    return (
      <div className="py-12 text-center" role="status">
        <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin">
          <span className="sr-only">Laden...</span>
        </div>
      </div>
    );
  }

  const allGroups = groupLinesByPicking(pickings, lines, moves);
  const leveringen = allGroups.filter((g) => g.picking.picking_type_code !== 'incoming');
  const retours = allGroups.filter((g) => g.picking.picking_type_code === 'incoming');

  const totalArticles = allGroups.reduce((sum, g) => sum + g.lines.length + g.addedMoves.length, 0);

  if (allGroups.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-td-muted text-sm">Geen materiaallijst gevonden</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Fase total */}
      <div className="rounded-xl bg-td-navy/5 px-3.5 py-2.5 flex items-center justify-between">
        <span className="text-xs font-semibold text-td-muted">
          {leveringen.length} {leveringen.length === 1 ? 'materiaallijst' : 'materiaallijsten'}
        </span>
        <span className="text-sm font-extrabold text-td-navy">
          {totalArticles} {totalArticles === 1 ? 'artikel' : 'artikelen'}
        </span>
      </div>

      {leveringen.map((group) => (
        <MateriaalLijstCard key={group.picking.id} group={group} isRetour={false} />
      ))}

      {retours.length > 0 && (
        <>
          <div className="flex items-center gap-1.5 px-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-amber-700">
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3M21 3v6h-6" />
            </svg>
            Retours (werf → magazijn)
          </div>
          {retours.map((group) => (
            <MateriaalLijstCard key={group.picking.id} group={group} isRetour />
          ))}
        </>
      )}
    </div>
  );
}
